import { useQuery } from '@tanstack/react-query';
import { Select, Statistic } from 'antd';
import React, { createContext, useContext, useState } from 'react';

import { REGIONS } from '../../constants/REGIONS';
import { fetchAPI } from '../../utils/api';
import { formatPriceNumber } from '../../utils/format';

const TotalPOContext = createContext(null);

export const TotalPOProvider: React.FC<{
  children: React.ReactNode;
  payload: any;
  currency?: string;
}> = ({ children, payload, currency: initialCurrency = 'SGD' }) => {
  const [currency, setCurrency] = useState(initialCurrency);

  React.useEffect(() => {
    setCurrency(initialCurrency || 'SGD');
  }, [initialCurrency]);

  const totalPOValue = useQuery(
    ['total-po-value', payload, currency],
    async () => {
      try {
        const resp = await fetchAPI('orders/values', {
          method: 'POST',
          payload: { ...payload, currency },
        });
        return resp;
      } catch {
        return {
          po_unfulfilled: 0,
          po_fulfilled: 0,
          client_paid_value: 0,
          fx_rate: 1,
          from_currency: 'SGD',
          to_currency: 'SGD',
        };
      }
    },
    {
      staleTime: 5 * 60 * 1000,
      placeholderData: {
        po_unfulfilled: 0,
        po_fulfilled: 0,
        client_paid_value: 0,
        fx_rate: 1,
        from_currency: 'SGD',
        to_currency: 'SGD',
      },
    }
  );

  return (
    <TotalPOContext.Provider
      value={{ currency, setCurrency, query: totalPOValue }}
    >
      {children}
    </TotalPOContext.Provider>
  );
};

// custom hooks to access total po context
export const useTotalPOValue = () => {
  const ctx = useContext(TotalPOContext);
  if (!ctx)
    throw new Error('useTotalPOValue must be used within CurrencyProvider');
  return ctx;
};

export const DropdownCurrency = (props: { title?: string }) => {
  const { currency, setCurrency } = useTotalPOValue();

  return (
    <Select
      style={{ width: 100 }}
      value={currency}
      onChange={value => setCurrency(value)}
      options={Object.values(REGIONS)
        .filter((curr, idx, arr) => {
          return arr.indexOf(curr) === idx;
        })
        .map(curr => ({
          label: curr,
          value: curr,
        }))}
    />
  );
};

const OrderTotalPOValues = () => {
  const { query, currency } = useTotalPOValue();

  return (
    <>
      <Statistic
        title="Total PO Unfulfilled"
        value={formatPriceNumber(query.data?.po_unfulfilled || 0, currency)}
        style={{
          display: 'inline-block',
          width: 'fit-content',
        }}
      />
    </>
  );
};

export const OrderTotalPOFulfilledValues = () => {
  const { query, currency } = useTotalPOValue();

  return (
    <>
      <Statistic
        title="Total PO Fulfilled"
        value={formatPriceNumber(query.data?.po_fulfilled || 0, currency)}
        style={{
          display: 'inline-block',
          width: 'fit-content',
        }}
      />
    </>
  );
};

export const OrderTotalClientPaidValues = () => {
  const { query, currency } = useTotalPOValue();

  return (
    <>
      <Statistic
        title={`Client Paid Total`}
        value={formatPriceNumber(query.data?.client_paid_value || 0, currency)}
        style={{
          display: 'inline-block',
          width: 'fit-content',
        }}
      />
    </>
  );
};

export const OrderFXRates = () => {
  const { query } = useTotalPOValue();
  // Only show FX rate if from_currency is SGD and to_currency is different from SGD
  const shouldShowFXRate =
    query.data?.from_currency === 'SGD' && query.data?.to_currency !== 'SGD';

  if (!shouldShowFXRate) {
    return null;
  }

  return (
    <>
      <Statistic
        title={`FX Rate (${query.data?.from_currency} → ${query.data?.to_currency})`}
        value={query.data?.fx_rate?.toFixed(4) || 'N/A'}
        style={{
          display: 'inline-block',
          width: 'fit-content',
        }}
      />
    </>
  );
};

export default OrderTotalPOValues;
